/**
 * Les demandes de remboursement de provision : la liste de ce qu'il faut virer.
 *
 * Une liste de travail, pas un écran de paiement. Le système ne signe aucun
 * virement — il constate ceux qui sont sortis.
 *
 * ## Pourquoi la communication est mise en évidence
 *
 * C'est **la seule chose à recopier**, et c'est elle qui referme la boucle. Un
 * virement portant cette communication structurée se rapproche tout seul au
 * CODA suivant, et la demande passe à « virée » sans que personne n'y touche.
 * Sans elle, le virement tombe dans la file des dépenses à placer et demande
 * un geste manuel.
 *
 * D'où le bouton « copier » à côté de chaque ligne : ce n'est pas un confort,
 * c'est ce qui évite la faute de frappe qui coûterait le rapprochement.
 */

import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Loader2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  loadDemandes,
  refuserDemande,
  type DemandeDeRemboursementAdmin,
} from '../../../lib/payments/refundRequests.supabase';
import { messageErreur } from '../../../lib/payments/erreur';
import { dateCourte, euros } from './money';

export default function DemandesDeRemboursement({
  rafraichir,
  onCount,
}: {
  rafraichir: number;
  onCount?: (nombre: number) => void;
}) {
  const [demandes, setDemandes] = useState<DemandeDeRemboursementAdmin[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [copiee, setCopiee] = useState<string | null>(null);
  const [voirToutes, setVoirToutes] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const liste = await loadDemandes(
        voirToutes ? ['en_attente', 'payee', 'refusee', 'annulee'] : ['en_attente'],
      );
      setDemandes(liste);
      setErreur(null);
      onCount?.(liste.filter((d) => d.status === 'en_attente').length);
    } catch (e) {
      setErreur(messageErreur(e, 'Impossible de charger les demandes'));
    } finally {
      setChargement(false);
    }
  }, [voirToutes, onCount]);

  useEffect(() => { void charger(); }, [charger, rafraichir]);

  const copier = useCallback(async (d: DemandeDeRemboursementAdmin) => {
    try {
      await navigator.clipboard.writeText(d.communication);
      setCopiee(d.id);
      window.setTimeout(() => setCopiee(null), 2000);
    } catch {
      toast.error('La copie a échoué — sélectionnez la communication à la main.');
    }
  }, []);

  const refuser = useCallback(async (d: DemandeDeRemboursementAdmin) => {
    const motif = window.prompt(
      `Refuser la demande de ${euros(d.amountCents)} ? Le montant retourne aux avoirs de la famille.\n\nMotif :`,
    );
    if (motif === null) return;
    try {
      await refuserDemande(d.id, motif);
      toast.success('Demande refusée — le montant est rendu aux avoirs');
      await charger();
    } catch (e) {
      toast.error(messageErreur(e, 'Le refus a été refusé'));
    }
  }, [charger]);

  const enAttente = demandes.filter((d) => d.status === 'en_attente');
  const total = enAttente.reduce((s, d) => s + d.amountCents, 0);

  if (chargement && demandes.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement des demandes…
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-700">
            {enAttente.length === 0
              ? 'Aucune demande en attente.'
              : <><strong>{enAttente.length}</strong> demande(s) à virer, <strong>{euros(total)}</strong>.</>}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            Recopiez la communication sur le virement : il se rapprochera tout seul au prochain
            import CODA. Les remboursements se font le 1er et le 15 de chaque mois.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={voirToutes}
            onChange={(e) => setVoirToutes(e.target.checked)}
            className="rounded border-gray-300"
          />
          Voir aussi les demandes closes
        </label>
      </div>

      {erreur && (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{erreur}</p>
      )}

      {demandes.length === 0 ? (
        <p className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          Rien à virer. Les familles qui ont des avoirs les laissent réduire leur prochaine facture.
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
          {demandes.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-medium tabular-nums text-gray-900">{euros(d.amountCents)}</span>
                  <span className="text-sm text-gray-600">{dateCourte(d.createdAt)}</span>
                  {d.status !== 'en_attente' && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                      {d.status === 'payee' ? 'virée' : d.status}
                    </span>
                  )}
                  {d.placedCents > 0 && d.placedCents < d.amountCents && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      {euros(d.placedCents)} déjà placés
                    </span>
                  )}
                </div>
                <p className="mt-1 font-mono text-sm text-indigo-700">{d.communication}</p>
                {(d.iban || d.accountHolder) && (
                  <p className="text-xs text-gray-500">
                    {d.accountHolder}{d.accountHolder && d.iban && ' · '}{d.iban}
                  </p>
                )}
                {d.motif && <p className="text-xs text-gray-500">Motif : {d.motif}</p>}
              </div>

              {d.status === 'en_attente' && (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => void copier(d)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {copiee === d.id
                      ? <><Check className="h-4 w-4 text-emerald-600" /> Copiée</>
                      : <><Copy className="h-4 w-4" /> Copier</>}
                  </button>
                  <button
                    onClick={() => void refuser(d)}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-700"
                    title="Refuser rend le montant aux avoirs de la famille."
                  >
                    <XCircle className="h-4 w-4" /> Refuser
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
